<script setup lang="ts">
import { BaseTable, TableColumn } from '@/components/Table'
import TableActionColumn from '@/components/Table/TableActionColumn.vue'
import TagStack from '@/components/Tags/TagStack.vue'
import { TypographyItem } from '@/components/Typography'
import type { UserState } from '@/store/userStore/UserStoreTypes'
import type { PaginationDataType } from '@/types/PaginationType'
import type { UserModel } from '@/types/models/UserModel'
import type { PermissionsEnums } from '@/types/permissions/PermissionsEnum'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

type Props = {
    data: PaginationDataType<UserState>
    loading?: boolean
    editPermission?: PermissionsEnums
    deletePermission?: PermissionsEnums

    editButtonHref?: string
}
defineProps<Props>()
type Emits = {
    (e: 'click', user: UserModel): void
    (e: 'delete', user: UserModel): void
}
const emit = defineEmits<Emits>()

const columns = computed(() => ({
    id: { field: 'id', label: t('organization.organizations.table.id') },
    name: { field: 'name', label: t('organization.organizations.table.name') },
    email: { field: 'email', label: t('organization.organizations.table.email') },
    roles: { field: 'roles', label: t('organization.organizations.table.roles') }
}))

const table = ref<any>([])
</script>

<template>
    <BaseTable v-bind:ref="table" :data="data.data.users ?? []" :loading="loading">
        <template #columns>
            <TableColumn :label="columns['id'].label" field="id" />
            <TableColumn :label="columns['name'].label" field="name" />
            <TableColumn :label="columns['email'].label" field="email" />
            <TableColumn :label="columns['roles'].label" v-slot="props">
                <TagStack
                    v-if="props.row.roles?.length"
                    :data="props.row.roles?.map((item: string) => ({ name: item }))"
                    :closable="false"
                    class="tag-stack" />
                <TypographyItem v-else :label="$t('common.noRole')" />
            </TableColumn>
            <TableActionColumn
                @goTo="$emit('click', $event)"
                @delete="emit('delete', $event)"
                :deletePermission="deletePermission"
                :editPermission="editPermission"
                :editButtonHref="editButtonHref"
                hrefKey="user_id" />
        </template>
    </BaseTable>
</template>

<style scoped lang="scss">
.tag-stack {
    & :hover {
        background-color: $primary;
    }
}
</style>
